/* eslint-disable no-empty-function */
/* eslint-disable @typescript-eslint/no-empty-function */
import * as React from 'react'
import cx from 'classnames'
import * as styles from './expander.policies.styl'
import { useDispatch, useSelector } from 'react-redux'
import * as PoliciesActions from '@src/redux/actions/policies.actions'
import type { State } from '@src/redux/configureStore'
import type { ExtRowInfo, ColumnProps } from '../../data.grid/models'
import { CSSSelector } from '@src/utils/css.selector.helper'
import { Policy, DisplayPolicy } from './partials/policy'
import { map, isArray, isEmpty, compact, uniq } from 'lodash'
import { PolicyDetails } from '@src/endpoints/services/base.service'
import LoadingDots from '../loading.dots'
import { idSuffix, parsePolicies } from '@src/utils/policy.utils'
import type { Policies } from '@src/utils/policy.utils'
import { parseCamURI } from '@src/utils/url.utils'
import { getPolicyDocuments } from './helpers'

export interface Props<T> {
  identity_uri: string | ((data: T) => string)
  row: ExtRowInfo<T>
  column: ColumnProps<T>
  className?: string
  onDataFetch?: (callback?: (res) => void) => void
}

export const CSSSelectors = {
  policySelector: new CSSSelector('policy')
}

interface PolicyProps {
  id?: string
  idp?: string
  roleName?: string
}
export interface PolicyItem extends PolicyProps {
  data: PolicyDetails
}

interface LocalState {
  shortPoliciesFetched?: boolean
  shortPoliciesData?: PolicyItem[]
  policies?: string[]
  requestPending?: boolean
  dataFetched?: boolean
}

const uiPolicyRegex = new RegExp(`(.+)\\s${idSuffix}:(.+)`)
const unfetchedPolicyRegex = new RegExp(
  `(LOOKUP|[A-Z]*=[a-zA-Z0-9-_]*):(.+)\\s${idSuffix}:(.+)`
)

interface ExpanderPoliciesData {
  via_policy?: string[]
  resource_uri?: string
  uri?: string
  entity_id?: string
  policies?: string[]
  resourceAccountID?: string
  identityAccountID?: string
  account_id?: string
  policy_account_id?: string
}

function ExpanderPolicies<T extends ExpanderPoliciesData>(props: Props<T>) {
  const dispatch = useDispatch()
  const policiesState = useSelector((state: State) => state.policies)
  const [localState, setLocalState] = React.useState<LocalState>({
    shortPoliciesFetched: false,
    shortPoliciesData: [],
    policies: [],
    requestPending: false,
    dataFetched: false
  })

  const { className, row, onDataFetch, identity_uri } = props
  const identityUri =
    typeof identity_uri === 'function'
      ? identity_uri(row?.original)
      : identity_uri

  if (
    typeof onDataFetch === 'function' &&
    !localState.dataFetched &&
    !localState.requestPending
  ) {
    const callback = res => {
      setLocalState(prev => ({
        ...prev,
        policies: res || [],
        requestPending: false,
        dataFetched: true
      }))
    }
    onDataFetch(callback)
    setLocalState(prev => ({ ...prev, requestPending: true }))
  }

  let policies: string[] = []
  const { policies: policy_list, via_policy } = row?.original

  if (localState.dataFetched && localState.policies.length > 0) {
    if (localState.dataFetched) {
      policies = parsePolicies(localState.policies as Policies) as string[]
    }
  }

  if (!localState.dataFetched) {
    if (policy_list !== undefined && isArray(policy_list)) {
      policies = policy_list
    } else if (via_policy !== undefined) {
      if (isArray(via_policy)) {
        policies = via_policy
      } else {
        policies = [via_policy]
      }
    } else {
      policies = []
    }
    setLocalState(prev => ({
      ...prev,
      dataFetched: true,
      policies
    }))
  }

  React.useEffect(() => () => {}, [policiesState])

  function getAccountIds(): string[] {
    const {
      resourceAccountID,
      identityAccountID,
      account_id,
      policy_account_id
    } = row.original
    return compact([
      resourceAccountID,
      identityAccountID,
      account_id,
      policy_account_id
    ])
  }

  function onClickPolicy(item: string, identity_uri: string) {
    const accountIds = getAccountIds()
    const policyOwnerId = parseCamURI(item)?.accountId
    const accountList = isEmpty(policyOwnerId)
      ? accountIds
      : [...accountIds, policyOwnerId]
    dispatch(
      PoliciesActions.setSelectedPolicy(item, uniq(accountList), identity_uri)
    )
  }

  function renderPolicies(
    policies: DisplayPolicy[] = [],
    identity_uri: string,
    onClick: (item: string, identity_uri: string) => void
  ) {
    const role = CSSSelectors.policySelector.value

    return map(policies, (item, index) => {
      {
        if (!isEmpty(item)) {
          return (
            <Policy
              identity_uri={identity_uri}
              attr={{ role, 'data-index': index.toString() }}
              item={item}
              key={index}
              onClick={onClick}
            />
          )
        }
        if (isArray(item) && item.length > 0) {
          return (
            <div
              role={`group_policies`}
              data-index={index}
              className={styles.line}
              key={index}
            >
              {renderPolicies(item, identity_uri, onClick)}
            </div>
          )
        }
        return 'N/A'
      }
    })
  }

  function formatDisplayPolicy(policy: string): DisplayPolicy {
    const result = policy.match(uiPolicyRegex)
    if (!isEmpty(result)) {
      const [, title, id] = result
      return { id, title }
    }
    return { id: policy, title: policy }
  }

  const policiesToFetch: PolicyProps[] = []
  const policiesReady: string[] = []

  if (localState.dataFetched) {
    ;(policies || []).forEach(p => {
      const parts = p.match(unfetchedPolicyRegex)
      if (!isEmpty(parts) && parts.length > 1) {
        if (parts[1] === 'LOOKUP' && !isEmpty(parts[2])) {
          policiesToFetch.push({ id: parts[2] })
        } else if (parts[1].split('=').length === 2) {
          const paramParts = parts[1].split('=')
          if (paramParts[0] === 'IDP') {
            policiesToFetch.push({ idp: paramParts[1], id: parts[2] })
          }
          if (paramParts[0] === 'ROLE') {
            policiesToFetch.push({ roleName: paramParts[1], id: parts[2] })
          }
        }
      } else {
        policiesReady.push(p)
      }
    })

    if (!isEmpty(policiesToFetch) && !localState.shortPoliciesFetched) {
      getPolicyDocuments(
        policiesToFetch.map(p => p.id),
        getAccountIds(),
        identityUri,
        res => {
          if (res.policies) {
            const stateData: PolicyItem[] = []
            policiesToFetch.forEach(p => {
              const i = res?.policies.findIndex(f => f.policy_name === p.id)
              if (i >= 0) {
                stateData.push({
                  data: res?.policies[i],
                  id: p.id,
                  idp: p.idp,
                  roleName: p.roleName
                })
              }
            })
            setLocalState(prev => ({
              ...prev,
              shortPoliciesData: stateData,
              shortPoliciesFetched: true
            }))
          } else {
            setLocalState(prev => ({
              ...prev,
              shortPoliciesData: policiesToFetch.map(
                p =>
                  ({
                    id: p.id,
                    data: {
                      policy_document: '{}'
                    }
                  } as PolicyItem)
              ),
              shortPoliciesFetched: true
            }))
          }
        }
      )
    }
  }
  const policiesList = localState?.policies?.map(p => formatDisplayPolicy(p))
  return (
    <div role={`expanded_policies`} className={cx(styles.wrapper, className)}>
      {((!isEmpty(policiesToFetch) && !localState.shortPoliciesFetched) ||
        localState.requestPending) && <LoadingDots loading />}
      {(isEmpty(policiesToFetch) ||
        (!isEmpty(policiesToFetch) && localState.shortPoliciesFetched)) &&
        renderPolicies(policiesList, identityUri, onClickPolicy)}
    </div>
  )
}

export default React.memo(ExpanderPolicies)
